import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saidaService, DetalhesRetorno, ProdutoSaidaResumido, retornoService } from '../../services/saida';
import DatePickerField from '../common/DatePickerField';

interface FormaPagamento {
  id: number;
  nome: string;
}

interface Pagamento {
  id?: number;
  idFormaPagamento: number;
  formaPagamento: string;
  valorPago: string;
  dataPagamento: string;
}

interface ValoresCalculados {
  valorDevolvidoComComissao: number;
  valorDevolvidoSemComissao: number;
  valorVendidoComComissao: number;
  valorVendidoSemComissao: number;
  totalVendido: number;
  valorAcerto: number;
  porcentagemVendedor: number;
  saldoAtual: number;
}

const FORMAS_PAGAMENTO: FormaPagamento[] = [
  { id: 1, nome: 'Dinheiro' },
  { id: 2, nome: 'Cartão' },
  { id: 3, nome: 'Depósito' },
  { id: 4, nome: 'Transferência' },
  { id: 5, nome: 'Cheque' },
  { id: 6, nome: 'PIX' },
];

const RetornoForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const idSaida = Number(id);

  const [dataRetorno, setDataRetorno] = useState('');
  const [produtos, setProdutos] = useState<ProdutoSaidaResumido[]>([]);
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [valorPagamento, setValorPagamento] = useState('');
  const [idFormaPagamento, setIdFormaPagamento] = useState('');
  const [dataPagamento, setDataPagamento] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const [valorDevolvidoComComissao, setValorDevolvidoComComissao] = useState(0);
  const [valorDevolvidoSemComissao, setValorDevolvidoSemComissao] = useState(0);
  const [valorVendidoComComissao, setValorVendidoComComissao] = useState(0);
  const [valorVendidoSemComissao, setValorVendidoSemComissao] = useState(0);
  const [valorAcerto, setValorAcerto] = useState(0);
  const [porcentagemVendedor, setPorcentagemVendedor] = useState(30);
  const [saldoAtual, setSaldoAtual] = useState(0);

  const { data: detalhes, isLoading, error } = useQuery<DetalhesRetorno>({
    queryKey: ['saida', 'retorno', idSaida],
    queryFn: () => saidaService.buscarDetalhesRetorno(idSaida),
    enabled: !!idSaida,
  });

  useEffect(() => {
    if (detalhes?.produtosResumidos) {
      const produtosInicializados = detalhes.produtosResumidos.map((p) => ({
        ...p,
        quantidadeDevolvida: p.quantidadeDevolvida ?? 0,
      }));
      setProdutos(produtosInicializados);
      calcularValores(produtosInicializados, detalhes);
    }
  }, [detalhes]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace('R$', '').replace('.', '').replace(',', '.')) || 0;
  };

  const calcularValores = (
    listaProdutos: ProdutoSaidaResumido[],
    detalhesSaida: DetalhesRetorno = detalhes!
  ): ValoresCalculados => {
    let devolvidoCom = 0;
    let devolvidoSem = 0;
    let vendidoCom = 0;
    let vendidoSem = 0;

    listaProdutos.forEach((p) => {
      const qtdDevolvida = Number(p.quantidadeDevolvida) || 0;
      const preco = Number(p.precoVenda) || 0;
      const qtdVendida = p.quantidade - qtdDevolvida;
      const semComissao = p.semComissao === 1 || p.semComissao === true;

      if (semComissao) {
        devolvidoSem += qtdDevolvida * preco;
        vendidoSem += qtdVendida * preco;
      } else {
        devolvidoCom += qtdDevolvida * preco;
        vendidoCom += qtdVendida * preco;
      }
    });

    setValorDevolvidoComComissao(devolvidoCom);
    setValorDevolvidoSemComissao(devolvidoSem);

    // Fallback: se não houver devoluções, usa os totais da sacola
    const totalCom = detalhesSaida?.valorTotalMercadoriaComComissao || 0;
    const totalSem = detalhesSaida?.valorTotalMercadoriaSemComissao || 0;
    const finalVendidoCom = vendidoCom || totalCom - devolvidoCom;
    const finalVendidoSem = vendidoSem || totalSem - devolvidoSem;

    const valorVendidoComComissao = Number(finalVendidoCom.toFixed(2));
    const valorVendidoSemComissao = Number(finalVendidoSem.toFixed(2));
    const totalVendido = valorVendidoComComissao + valorVendidoSemComissao;

    setValorVendidoComComissao(valorVendidoComComissao);
    setValorVendidoSemComissao(valorVendidoSemComissao);

    let percentual = 30;
    if (totalVendido >= 1000) {
      percentual = 40;
    } else if (totalVendido >= 400) {
      percentual = 35;
    }
    setPorcentagemVendedor(percentual);

    const porcentagem = Number((1 - percentual / 100).toFixed(2));
    const valorAcerto = Number(
      (valorVendidoComComissao * porcentagem + valorVendidoSemComissao).toFixed(2)
    );
    const totalPago = pagamentos.reduce((total, pagamento) => total + parseCurrencyInput(pagamento.valorPago), 0);
    const saldoAtual = Number((valorAcerto - totalPago).toFixed(2));

    setValorAcerto(valorAcerto);
    setSaldoAtual(saldoAtual);

    return {
      valorDevolvidoComComissao: devolvidoCom,
      valorDevolvidoSemComissao: devolvidoSem,
      valorVendidoComComissao,
      valorVendidoSemComissao,
      totalVendido,
      valorAcerto,
      porcentagemVendedor: percentual,
      saldoAtual,
    };
  };

  const handleAtualizarValores = () => {
    calcularValores(produtos);
  };

  const updateQuantidadeDevolvida = (index: number, value: number) => {
    const novos = [...produtos];
    novos[index] = { ...novos[index], quantidadeDevolvida: value };
    setProdutos(novos);
  };

  const handleAdicionarPagamento = () => {
    const valoresAtualizados = calcularValores(produtos);
    const valor = parseCurrencyInput(valorPagamento);
    if (!valor || !idFormaPagamento || !dataPagamento) {
      setSnackbar({ open: true, message: 'Preencha valor, forma de pagamento e data.', severity: 'error' });
      return;
    }
    if (valor > valoresAtualizados.saldoAtual) {
      setSnackbar({ open: true, message: 'O valor não pode ser maior que o saldo.', severity: 'error' });
      return;
    }

    const forma = FORMAS_PAGAMENTO.find((f) => f.id === Number(idFormaPagamento));
    const novoPagamento: Pagamento = {
      idFormaPagamento: Number(idFormaPagamento),
      formaPagamento: forma?.nome || '',
      valorPago: valorPagamento,
      dataPagamento,
    };
    setPagamentos([...pagamentos, novoPagamento]);
    setSaldoAtual(Number((valoresAtualizados.saldoAtual - valor).toFixed(2)));
    setValorPagamento('');
    setIdFormaPagamento('');
    setDataPagamento('');
  };

  const removerPagamento = (index: number) => {
    const pagamento = pagamentos[index];
    const valor = parseCurrencyInput(pagamento.valorPago);
    setPagamentos(pagamentos.filter((_, i) => i !== index));
    setSaldoAtual(Number((saldoAtual + valor).toFixed(2)));
  };

  const convertToApiDate = (dateString: string): string => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}T00:00:00`;
  };

  const saveMutation = useMutation({
    mutationFn: async (valoresAtualizados: ValoresCalculados) => {
      const retorno = {
        IdSaida: idSaida,
        DataRetorno: dataRetorno ? convertToApiDate(dataRetorno) : '',
        IdVendedor: detalhes?.idVendedor || 0,
        TotalVenda: valoresAtualizados.totalVendido,
        ValorASerPago: valoresAtualizados.valorAcerto,
        TotalPago: 0,
        PercentualVendedor: valoresAtualizados.porcentagemVendedor,
        ProdutosDevolvidos: produtos.map((p) => ({
          ...p,
          quantidadeDevolvida: Number(p.quantidadeDevolvida) || 0,
        })),
        Pagamentos: pagamentos.map((p) => ({
          ValorPago: parseCurrencyInput(p.valorPago),
          IdFormaPagamento: p.idFormaPagamento,
          DataPagamento: convertToApiDate(p.dataPagamento),
        })),
      };
      return retornoService.salvar(retorno as any);
    },
    onSuccess: (data: any) => {
      if (Array.isArray(data)) {
        setSnackbar({ open: true, message: data[0]?.title + ' - ' + data[0]?.detail, severity: 'error' });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      setSnackbar({ open: true, message: 'Retorno salvo com sucesso.', severity: 'success' });
      navigate('/saidas');
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro ao salvar retorno: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dataRetorno) {
      setSnackbar({ open: true, message: 'Data de retorno é obrigatória.', severity: 'error' });
      return;
    }
    const valoresAtualizados = calcularValores(produtos);
    saveMutation.mutate(valoresAtualizados);
  };

  const handleBack = () => {
    navigate('/saidas');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !detalhes) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar detalhes da saída.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar detalhes da saída.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Retorno - Saída #{idSaida}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          {/* Dados da saída */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="Funcionário" value={detalhes.funcionario || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Praça" value={detalhes.praca || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="CPF" value={detalhes.cpf || ''} slotProps={{ input: { readOnly: true } }} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="Vendedor(a)" value={detalhes.vendedor || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Data Saída" value={detalhes.dataSaida || ''} slotProps={{ input: { readOnly: true } }} />
            <DatePickerField label="Data Retorno" value={dataRetorno} onChange={setDataRetorno} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="Telefone" value={detalhes.telefone || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Endereço" value={detalhes.endereco || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Nº" value={detalhes.numero || ''} slotProps={{ input: { readOnly: true } }} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="Bairro" value={detalhes.bairro || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Cidade" value={detalhes.cidade || ''} slotProps={{ input: { readOnly: true } }} />
            <TextField label="UF" value={detalhes.estado || ''} slotProps={{ input: { readOnly: true } }} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField
              label="Valor total da sacola (produtos com comissão)"
              value={formatCurrency(detalhes.valorTotalMercadoriaComComissao)}
              slotProps={{ input: { readOnly: true } }}
            />
            <TextField
              label="Valor total da sacola (produtos sem comissão)"
              value={formatCurrency(detalhes.valorTotalMercadoriaSemComissao)}
              slotProps={{ input: { readOnly: true } }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="Valor devolvido com comissão" value={formatCurrency(valorDevolvidoComComissao)} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Valor devolvido sem comissão" value={formatCurrency(valorDevolvidoSemComissao)} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Valor vendido com comissão" value={formatCurrency(valorVendidoComComissao)} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Valor vendido sem comissão" value={formatCurrency(valorVendidoSemComissao)} slotProps={{ input: { readOnly: true } }} />
            <TextField label="Valor acerto" value={formatCurrency(valorAcerto)} slotProps={{ input: { readOnly: true } }} />
          </Box>
        </Paper>

        {/* Produtos */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Produtos
            </Typography>
            <Button
              type="button"
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleAtualizarValores}
            >
              Atualizar valores
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Qtd em saída</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sem Comissão</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor unitário</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Qtd devolvida</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Qtd vendida</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total vendido</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {produtos.map((produto, index) => {
                  const qtdDevolvida = Number(produto.quantidadeDevolvida) || 0;
                  const qtdVendida = produto.quantidade - qtdDevolvida;
                  const totalVendido = qtdVendida * (Number(produto.precoVenda) || 0);
                  return (
                    <TableRow key={produto.id}>
                      <TableCell>{produto.referencia}</TableCell>
                      <TableCell>{produto.nomeProduto}</TableCell>
                      <TableCell>{produto.quantidade}</TableCell>
                      <TableCell>{produto.semComissao === 1 || produto.semComissao === true ? 'SIM' : 'NÃO'}</TableCell>
                      <TableCell>{formatCurrency(Number(produto.precoVenda))}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={produto.quantidadeDevolvida}
                          onChange={(e) => updateQuantidadeDevolvida(index, Number(e.target.value))}
                          sx={{ width: 80 }}
                          slotProps={{ htmlInput: { min: 0, max: produto.quantidade } }}
                        />
                      </TableCell>
                      <TableCell>{qtdVendida}</TableCell>
                      <TableCell>{formatCurrency(totalVendido)}</TableCell>
                    </TableRow>
                  );
                })}
                {produtos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>Nenhum produto encontrado.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Pagamentos */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Pagamentos
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr auto' }, gap: 2, mb: 2 }}>
            <TextField label="Saldo" value={formatCurrency(saldoAtual)} slotProps={{ input: { readOnly: true } }} />
            <TextField
              label="Valor pago"
              value={valorPagamento}
              onChange={(e) => setValorPagamento(e.target.value)}
              placeholder="R$ 0,00"
            />
            <FormControl fullWidth>
              <InputLabel>Forma de pagamento</InputLabel>
              <Select value={idFormaPagamento} onChange={(e) => setIdFormaPagamento(e.target.value)} label="Forma de pagamento">
                {FORMAS_PAGAMENTO.map((forma) => (
                  <MenuItem key={forma.id} value={forma.id}>
                    {forma.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePickerField label="Data pagamento" value={dataPagamento} onChange={setDataPagamento} />
            <IconButton color="primary" onClick={handleAdicionarPagamento} sx={{ height: 40, alignSelf: 'center' }}>
              <AddIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Forma de pagamento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagamentos.map((pagamento, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatCurrency(parseCurrencyInput(pagamento.valorPago))}</TableCell>
                    <TableCell>{pagamento.formaPagamento}</TableCell>
                    <TableCell>{pagamento.dataPagamento}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removerPagamento(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {pagamentos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={{ py: 2, color: 'text.secondary' }}>Nenhum pagamento adicionado.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Botões */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button variant="contained" type="submit" startIcon={<SaveIcon />} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetornoForm;
